import { calculateSubscriptionPrice } from 'components/cart/utils/calculateSubscriptionPrice';
import type { Cart } from 'types/cart/Cart';
import type { CartLineItem } from 'types/cart/CartLineItem';
import { PurchaseType } from 'types/product/PurchaseType';
import type { SubscriptionProduct } from 'types/product/SubscriptionProduct';
import { getProductVariant } from 'utils/product/getProductVariant';

import type { CartUpdateItem } from '../schema';

export interface GaEcommerceItem {
  item_id: string;
  item_name: string;
  item_category: string;
  item_variant: string;
  price: number;
  quantity: number;
}

export interface GaLineItem {
  sku: string;
  name: string;
  variant: string;
  purchaseType: PurchaseType;
  quantity: number;
  price: number;
}

export const ecommerceItemPayload = ({
  sku,
  name,
  purchaseType,
  variant,
  price,
  quantity,
}: GaLineItem): GaEcommerceItem => ({
  item_id: sku,
  item_name: name,
  item_category: purchaseType,
  item_variant: variant,
  price,
  quantity,
});

export const itemPayloadFromLineItem = ({
  lineItem,
  lineItem: {
    quantity,
    sellingPlanAllocation,
    merchandise,
    merchandise: { product },
  },
}: {
  lineItem: CartLineItem;
}) => {
  const isOneTimePurchase = !sellingPlanAllocation;

  return ecommerceItemPayload({
    sku: merchandise.sku as string,
    name: product.title,
    variant: merchandise.title,
    purchaseType: isOneTimePurchase ? PurchaseType.OTP : PurchaseType.SUB,
    quantity,
    price: isOneTimePurchase
      ? merchandise.price.amount
      : (calculateSubscriptionPrice(lineItem)?.amount as number),
  });
};

export const itemPayloadFromProduct = ({
  product,
  merchandiseId,
  quantity,
}: CartUpdateItem) => {
  /* INFO: ShopifyProduct is not a discriminated union type,
   * hence the need for the cast. */
  const isOneTimePurchase =
    !(product as SubscriptionProduct)?.subscriptions ||
    (product as SubscriptionProduct)?.subscriptions.length === 0;

  const variant = getProductVariant(product, merchandiseId);

  if (!variant || !variant.sku) return null;

  return ecommerceItemPayload({
    sku: variant.sku,
    name: product.title,
    variant: variant.title,
    purchaseType: isOneTimePurchase ? PurchaseType.OTP : PurchaseType.SUB,
    quantity,
    price: variant.price.amount,
  });
};

export const createEcommercePayload = ({ totalPrice, lines }: Cart) => ({
  currency: totalPrice.currencyCode,
  value: totalPrice.amount,
  items: lines.map((li) => itemPayloadFromLineItem({ lineItem: li })),
});
